import { useEffect, useLayoutEffect, useRef } from 'react';
import { subscribeDiscoveryReconnect } from './discoveryConnectivity';

// Effect-style loader: use signal for transport cancellation and isCurrent()
// before success/error/finally state writes. Reconnect supersedes an outstanding
// request immediately; it never waits for a disconnected request to settle.
export default function useDiscoveryFetch(load, dependencies, { reconnect = true } = {}) {
    const latestLoad = useRef(load);
    useLayoutEffect(() => { latestLoad.current = load; });

    useEffect(() => {
        let controller;
        let cleanup;
        let disposed = false;
        const run = (isReconnect = false) => {
            if (disposed) return;
            controller?.abort();
            cleanup?.();
            const request = new AbortController();
            controller = request;
            cleanup = latestLoad.current({
                signal: request.signal,
                isCurrent: () => !disposed && !request.signal.aborted,
                isReconnect,
            });
        };
        const unsubscribe = reconnect ? subscribeDiscoveryReconnect(() => run(true)) : () => {};
        run();
        return () => {
            disposed = true;
            unsubscribe();
            controller?.abort();
            cleanup?.();
        };
        // Route/resource identity controls initial loads; UI state is read from
        // the latest committed callback without fetching on every filter edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...dependencies, reconnect]);
}
